import { FeatureFlag } from "@/hooks/use-feature-flags";
import { BillingModelFeatures } from "@/lib/billing/types";

// Navigation item types
export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: string; // Lucide icon name
  featureFlag?: FeatureFlag;
  requiresAdmin?: boolean;
  requiresSuperAdmin?: boolean;
  requiresStripeConnect?: boolean;
  /** Hide this item if the billing model feature is false */
  requiresBillingFeature?: keyof BillingModelFeatures;
  /** Only show this item for team-tier subscriptions */
  requiresTeamTier?: boolean;
  /**
   * Hide this item if the user's subscription tier / addon doesn't grant the feature.
   * Keys must exist in `FEATURE_GATES` (see src/hooks/use-feature-access.tsx).
   */
  requiresTierFeature?: string;
  /** Optional badge to display next to the nav item (e.g. unread count) */
  badge?: string | number;
}

// Section nav item can have children (for expandable sub-menus)
export interface SectionNavItem extends NavItem {
  children?: NavItem[];
  /** Permanent items always appear in their section and cannot be removed */
  permanent?: boolean;
}

export interface NavGroup {
  id: string;
  label: string;
  icon: string;
  items: NavItem[];
  requiresAdmin?: boolean;
}

export type NavEntry = NavItem | NavGroup;

// Type guard to check if entry is a group
export function isNavGroup(entry: NavEntry): entry is NavGroup {
  return "items" in entry;
}

// Grouped navigation for sidebar
export interface SidebarNavSection {
  id: string;
  label: string; // Section header (e.g., "OVERVIEW", "CLIENTS")
  items: SectionNavItem[];
  requiresAdmin?: boolean;
  requiresSuperAdmin?: boolean;
}

// Sidebar navigation configuration - grouped sections
export const SIDEBAR_NAV_CONFIG: SidebarNavSection[] = [
  {
    id: "overview",
    label: "FAVORITES",
    items: [
      { id: "dashboard", label: "Dashboard", path: "/dashboard", icon: "LayoutDashboard", permanent: true },
      { id: "calendar", label: "Calendar", path: "/calendar", icon: "Calendar", permanent: true },
    ],
  },
  {
    id: "clients",
    label: "CLIENTS",
    items: [
      { id: "all-clients", label: "All Clients", path: "/clients", icon: "Users" },
      { id: "leads", label: "Leads", path: "/leads", icon: "UserPlus" },
      { id: "forecast", label: "Forecast", path: "/clients/forecast", icon: "TrendingUp", requiresBillingFeature: "showRevenueForecast", requiresTierFeature: "forecast" },
      { id: "messages", label: "Messages", path: "/messages", icon: "MessageCircle", featureFlag: "messages" },
      { id: "sms", label: "SMS", path: "/sms", icon: "Phone", featureFlag: "sms", requiresTierFeature: "sms_reminders" },
    ],
  },
  {
    id: "training",
    label: "TRAINING",
    items: [
      { id: "programs", label: "Programs", path: "/programs", icon: "Dumbbell", featureFlag: "workouts" },
      { id: "video-library", label: "Videos", path: "/video-library", icon: "Video" },
      { id: "habits", label: "Habits", path: "/habits", icon: "ListChecks" },
      { id: "templates", label: "Templates", path: "/templates", icon: "LayoutTemplate" },
      { id: "check-ins", label: "Check-ins", path: "/check-ins", icon: "ClipboardList" },
      { id: "health-hub", label: "Health Hub", path: "/health", icon: "HeartPulse", featureFlag: "health_hub", requiresTierFeature: "health_hub" },
      { id: "routes", label: "Routes", path: "/routes", icon: "Route", featureFlag: "route_intelligence", requiresTierFeature: "route_optimization" },
      { id: "location-intel", label: "Location Intel", path: "/location-intelligence", icon: "MapPinned", featureFlag: "route_intelligence", requiresTierFeature: "route_optimization" },
    ],
  },
  {
    id: "business",
    label: "BUSINESS",
    items: [
      { id: "financials", label: "Financials", path: "/earnings", icon: "DollarSign", featureFlag: "earnings", requiresTierFeature: "earnings_tracking" },
      { id: "invoices", label: "Invoices", path: "/invoices", icon: "Receipt", requiresStripeConnect: true },
      { id: "insights", label: "Insights", path: "/insights", icon: "BarChart3", featureFlag: "pulse_check", requiresTierFeature: "pulse_check" },
      { id: "proactive-insights", label: "Tasks", path: "/proactive-insights", icon: "Sparkles" },
      { id: "financial-intelligence", label: "Intelligence", path: "/financial-intelligence", icon: "Lightbulb", featureFlag: "financial_intelligence", requiresTierFeature: "financial_intelligence" },
      { id: "simulations", label: "Simulations", path: "/simulations", icon: "FlaskConical", featureFlag: "financial_intelligence", requiresTierFeature: "financial_intelligence" },
      { id: "integrations", label: "Integrations", path: "/integrations", icon: "Plug" },
    ],
  },
  {
    id: "marketing",
    label: "GROW YOUR BUSINESS",
    // Ordered by frequency of use for a non-marketing-savvy trainer, per
    // competitor benchmark + persona research:
    // 1. Booking link (most-used across all competitor platforms)
    // 2. Automations (set-and-forget client touchpoints — Zen Planner play)
    // 3. Email Campaigns (broadcasts when trainer has something to say)
    // 4. Referrals (table stakes; simple code-sharing flow)
    // Then everything else, de-emphasized but still discoverable.
    items: [
      // Primary — the 4 things a trainer will actually use
      { id: "public-profile", label: "My Booking Link", path: "/profile", icon: "Link2", featureFlag: "marketing", requiresTierFeature: "marketing_suite" },
      { id: "campaigns", label: "Campaigns", path: "/marketing/campaigns", icon: "Megaphone", featureFlag: "marketing", requiresTierFeature: "marketing_suite" },
      { id: "automations", label: "Automations", path: "/marketing/automations", icon: "Zap", featureFlag: "marketing", requiresTierFeature: "marketing_suite" },
      { id: "email-campaigns", label: "Email Campaigns", path: "/marketing/emails", icon: "Send", featureFlag: "marketing", requiresTierFeature: "marketing_suite" },
      { id: "story-studio", label: "Story Studio", path: "/marketing/story-studio", icon: "Sparkles", featureFlag: "marketing", requiresTierFeature: "marketing_suite" },
      { id: "referrals", label: "Referrals", path: "/referrals", icon: "Gift", featureFlag: "marketing", requiresTierFeature: "referrals" },
      // Secondary — available but less prominent (trainer opts in)
      { id: "social-media", label: "Social Posts", path: "/marketing/social", icon: "AtSign", featureFlag: "marketing", requiresTierFeature: "marketing_suite" },
      { id: "landing-pages", label: "Landing Pages", path: "/marketing/pages", icon: "Globe", featureFlag: "marketing", requiresTierFeature: "marketing_suite" },
      { id: "main-site", label: "Main Site", path: "/marketing/site", icon: "Home", featureFlag: "marketing", requiresTierFeature: "marketing_suite" },
      { id: "media-library", label: "Media Library", path: "/marketing/media", icon: "Images", featureFlag: "marketing", requiresTierFeature: "marketing_suite" },
      { id: "ai-agent", label: "AI Assistant", path: "/marketing/agent", icon: "Bot", featureFlag: "marketing", requiresTierFeature: "marketing_agent" },
    ],
  },
  {
    id: "company",
    label: "COMPANY",
    requiresAdmin: true,
    items: [
      { id: "company-dashboard", label: "Overview", path: "/company", icon: "Building2", requiresTierFeature: "company_dashboard" },
      { id: "team", label: "Team", path: "/team", icon: "UsersRound", requiresTierFeature: "team_seats" },
      { id: "employee-roi", label: "Employee ROI", path: "/employee-roi", icon: "Calculator", requiresTeamTier: true, requiresTierFeature: "team_seats" },
      { id: "community", label: "Community", path: "/community", icon: "Megaphone" },
      { id: "company-settings", label: "Company Settings", path: "/company/settings", icon: "Settings" },
      { id: "matching", label: "Matching", path: "/matching", icon: "Handshake", featureFlag: "marketplace", requiresTierFeature: "marketplace_listing" },
    ],
  },
  {
    id: "admin",
    label: "ADMIN",
    requiresSuperAdmin: true,
    items: [
      { id: "admin-dashboard", label: "Admin Panel", path: "/admin", icon: "Shield" },
    ],
  },
];

// Section navigation config (for page-level sidebars - kept for backwards compatibility)
export const SECTION_NAV_CONFIG: Record<string, SectionNavItem[]> = {
  clients: [
    { id: "all-clients", label: "All Clients", path: "/clients", icon: "Users" },
    { id: "client-reports", label: "Reports", path: "/clients/reports", icon: "BarChart2" },
    { id: "leads", label: "Leads", path: "/leads", icon: "UserPlus" },
    { id: "messages", label: "Messages", path: "/messages", icon: "MessageCircle", featureFlag: "messages" },
  ],
  operations: [
    { id: "financials", label: "Financials", path: "/earnings", icon: "DollarSign", featureFlag: "earnings" },
    { id: "invoices", label: "Invoices", path: "/invoices", icon: "Receipt", requiresStripeConnect: true },
    { id: "insights", label: "Insights", path: "/insights", icon: "BarChart3", featureFlag: "pulse_check" },
  ],
  company: [
    { id: "company-dashboard", label: "Dashboard", path: "/company", icon: "Building2" },
    { id: "team", label: "Team", path: "/team", icon: "UsersRound" },
    { id: "community", label: "Community", path: "/community", icon: "Megaphone" },
    { id: "company-settings", label: "Settings", path: "/company/settings", icon: "Settings" },
  ],
};

// Helper to get section nav items
export function getSectionNavItems(sectionId: string): SectionNavItem[] {
  return SECTION_NAV_CONFIG[sectionId] || [];
}

// Helper to determine which section a path belongs to
export function getSectionForPath(pathname: string): string | null {
  for (const [sectionId, items] of Object.entries(SECTION_NAV_CONFIG)) {
    if (items.some(item => pathname === item.path || pathname.startsWith(item.path + '/'))) {
      return sectionId;
    }
  }
  return null;
}

// Main navigation configuration - kept for backwards compatibility (mobile nav, etc.)
export const MAIN_NAV_CONFIG: NavItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    path: "/dashboard",
    icon: "LayoutDashboard",
  },
  {
    id: "calendar",
    label: "Calendar",
    path: "/calendar",
    icon: "Calendar",
  },
  {
    id: "clients",
    label: "Clients",
    path: "/clients",
    icon: "Users",
  },
  {
    id: "programs",
    label: "Programs",
    path: "/programs",
    icon: "Dumbbell",
    featureFlag: "workouts",
  },
  {
    id: "video-library",
    label: "Videos",
    path: "/video-library",
    icon: "Video",
  },
  {
    id: "habits",
    label: "Habits",
    path: "/habits",
    icon: "ListChecks",
  },
  {
    id: "templates",
    label: "Templates",
    path: "/templates",
    icon: "LayoutTemplate",
  },
  {
    id: "check-ins",
    label: "Check-ins",
    path: "/check-ins",
    icon: "ClipboardList",
  },
  {
    id: "operations",
    label: "Operations",
    path: "/earnings",
    icon: "Briefcase",
  },
  {
    id: "company",
    label: "Company",
    path: "/company",
    icon: "Building2",
    requiresAdmin: true,
  },
  {
    id: "admin",
    label: "Admin",
    path: "/admin",
    icon: "Shield",
    requiresSuperAdmin: true,
  },
];
